import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import AgentMark from './AgentMark.jsx';
import { AGENTS, AGENT_IDS } from '../agents.js';

export default function InitiationModal({ project }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const tabs = project ? ['brief', 'ask', 'new'] : ['new'];
  const [tab, setTab] = useState(tabs[0]);

  useEffect(() => {
    function onOpen() { setOpen(true); setTab(tabs[0]); }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    window.addEventListener('openInit', onOpen);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('openInit', onOpen); window.removeEventListener('keydown', onKey); };
  }, [project]);

  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={() => setOpen(false)}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-tabs">
          {tabs.includes('brief') && <button className={`modal-tab ${tab === 'brief' ? 'is-on' : ''}`} onClick={() => setTab('brief')}>Brief an agent</button>}
          {tabs.includes('ask')   && <button className={`modal-tab ${tab === 'ask'   ? 'is-on' : ''}`} onClick={() => setTab('ask')}>Ask the room</button>}
          {tabs.includes('new')   && <button className={`modal-tab ${tab === 'new'   ? 'is-on' : ''}`} onClick={() => setTab('new')}>Start a project</button>}
        </div>
        {tab === 'brief' && <BriefTab project={project} onDone={(t) => { setOpen(false); nav(`/thread/${t._id}`); }} onClose={() => setOpen(false)} />}
        {tab === 'ask'   && <AskTab   project={project} onDone={() => setOpen(false)} onClose={() => setOpen(false)} />}
        {tab === 'new'   && <NewProjectTab onDone={(p) => { setOpen(false); nav(`/project/${p._id}`); }} onClose={() => setOpen(false)} />}
      </div>
    </div>
  );
}

function BriefTab({ project, onDone, onClose }) {
  const [agent, setAgent] = useState('narrative');
  const [body, setBody] = useState('');
  async function submit(e) {
    e.preventDefault();
    const r = await api(`/api/projects/${project._id}/brief`, { method: 'POST', body: { agent, body } });
    onDone(r.thread);
  }
  return (
    <form onSubmit={submit}>
      <div className="modal-body">
        <h3>Brief an agent on <em>{project.title}</em></h3>
        <p className="sub">One agent. One ask. Goes into their thread as a new turn from you.</p>
        <div className="modal-row">
          <label>Agent</label>
          <div className="modal-agents">
            {AGENT_IDS.map(id => (
              <button type="button" key={id} className={`modal-agent ${agent === id ? 'is-on' : ''}`} onClick={() => setAgent(id)}>
                <AgentMark agent={AGENTS[id]} size="xs" />
                <div>
                  <div className="name">{AGENTS[id].name}</div>
                  <div className="role">{AGENTS[id].role}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className="modal-row">
          <label>Brief</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="What is the question?" />
        </div>
      </div>
      <div className="modal-foot">
        <span className="hint">Posts to the thread · agent will see it next.</span>
        <div className="actions">
          <button type="button" className="btn ghost small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small" disabled={!body.trim()}>Send brief</button>
        </div>
      </div>
    </form>
  );
}

function AskTab({ project, onDone, onClose }) {
  const [body, setBody] = useState('');
  async function submit(e) {
    e.preventDefault();
    await api(`/api/projects/${project._id}/ask-the-room`, { method: 'POST', body: { body } });
    onDone();
  }
  return (
    <form onSubmit={submit}>
      <div className="modal-body">
        <h3>Ask the room — <em>{project.title}</em></h3>
        <p className="sub">Broadcasts to all agents on this project. Each one responds in their own thread.</p>
        <div className="modal-row">
          <label>Question</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
            {AGENT_IDS.map(id => (
              <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 10px 5px 5px', border: '1px solid var(--rule-2)' }}>
                <AgentMark agent={AGENTS[id]} size="xs" />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink-2)' }}>{AGENTS[id].name}</span>
              </span>
            ))}
          </div>
          <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Pose a question to every agent." />
        </div>
      </div>
      <div className="modal-foot">
        <span className="hint">Broadcasts to all agents on this project.</span>
        <div className="actions">
          <button type="button" className="btn ghost small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small" disabled={!body.trim()}>Ask the room</button>
        </div>
      </div>
    </form>
  );
}

function NewProjectTab({ onDone, onClose }) {
  const [title, setTitle] = useState('');
  const [format, setFormat] = useState('immersive');
  const [logline, setLogline] = useState('');
  const [colorMark, setColorMark] = useState('#c8a16b');
  async function submit(e) {
    e.preventDefault();
    const p = await api('/api/projects', { method: 'POST', body: { title, format, colorMark, logline } });
    onDone(p);
  }
  return (
    <form onSubmit={submit}>
      <div className="modal-body">
        <h3>Start a project</h3>
        <p className="sub">A new silo. Its own agents, its own memory. No cross-contamination from anything else in the workshop.</p>
        <div className="modal-row">
          <label>Working title</label>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. The Pier Project" />
        </div>
        <div className="modal-row">
          <label>Format</label>
          <select value={format} onChange={e => setFormat(e.target.value)}>
            <option value="immersive">immersive</option>
            <option value="opera">opera</option>
            <option value="dance">dance</option>
            <option value="theatre">theatre</option>
            <option value="concert">concert</option>
          </select>
        </div>
        <div className="modal-row">
          <label>Color mark</label>
          <div className="colormark-pick">
            <input type="color" value={colorMark} onChange={e => setColorMark(e.target.value)} aria-label="Color mark" />
            <span className="colormark-hex">{colorMark}</span>
          </div>
        </div>
        <div className="modal-row">
          <label>The provocation (one sentence)</label>
          <textarea value={logline} onChange={e => setLogline(e.target.value)} placeholder="The single question that justifies a project existing." />
        </div>
      </div>
      <div className="modal-foot">
        <span className="hint">Creates an empty project. The agents pick up from here.</span>
        <div className="actions">
          <button type="button" className="btn ghost small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small" disabled={!title.trim()}>Open the project</button>
        </div>
      </div>
    </form>
  );
}
